import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the template edit-fields permission gate.
 *
 * Templates are a CSR/LC construct: per docs/Permission-analysis.md every
 * editable template field (Order Info / Addl Info / Tracing / Legs / the
 * approval switch) is CSR/LC-only — there is no per-field owner variation. So a
 * single component gates the whole template edit surface rather than one
 * container per field. Admins configure it once in permissions-mode; it is
 * trivial to split into finer components later if a field ever diverges.
 */
export const TEMPLATE_EDIT_FIELDS_COMPONENT_ID = 'template-edit-fields';

/**
 * Whether the current user may edit template fields.
 *
 * Resolved from the admin-configured component permission (permissions-mode
 * cog) crossed with the user's Azure AD groups. Ships **defaultDisabled**:
 * until an admin grants a group edit access on `template-edit-fields`,
 * templates render read-only for everyone (fallback level 1). Non-LC groups
 * stay read-only after configuration, which is the whole point of the gate.
 *
 * Only meaningful for templates — callers gate their template branch on this
 * and leave the normal order flow (its own permission containers) untouched.
 * Uses the lenient context lookups inside `useResolvedAccessLevel`, so in
 * provider-less test/preview trees it returns the fallback; production views
 * take the real value as a prop (defaulting to editable) so isolated renders
 * are unaffected.
 */
export const useCanEditTemplateFields = (): boolean =>
  useResolvedAccessLevel(TEMPLATE_EDIT_FIELDS_COMPONENT_ID, false, true) >= 2;
